import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  MagnifyingGlass,
  ArrowRight,
  Sparkle,
  Clock,
  CheckCircle,
  Archive,
  UserCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listInquiries,
  listOrgAdmins,
  getInquiryStats,
  INQUIRY_STATUSES,
  statusMeta,
} from "@/lib/inquiries";
import { inquiryAgeBucket, AGE_TONE, formatAgeShort } from "@/lib/inquiryFunnel";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin · Enrollment inquiries list.
 *
 * Shipping choices:
 *   * URL-driven filters (?status=new&q=smith) so admins can share a
 *     link to "this queue" and browser-back restores state.
 *   * One KPI strip at the top pulled from rpc_inquiry_stats — same
 *     source the admin Dashboard card uses.
 *   * No pagination yet — hard-caps at 100. Good enough until an org
 *     sees > 100 open inquiries; trivial to add later.
 */
export default function AdminInquiries() {
  const { profile } = useAuth();
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") || "";
  const q      = sp.get("q")      || "";
  const mine   = sp.get("mine") === "1";

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminLookup, setAdminLookup] = useState({}); // profile_id -> { full_name, email }

  // Local search input is decoupled from the URL so typing isn't laggy
  // (each keystroke would otherwise round-trip through React Router).
  const [input, setInput] = useState(q);

  useEffect(() => { setInput(q); }, [q]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    let alive = true;
    (async () => {
      const list = await listOrgAdmins(profile.organization_id);
      if (!alive) return;
      const map = {};
      list.forEach((a) => { map[a.profile_id] = a; });
      setAdminLookup(map);
    })();
    return () => { alive = false; };
  }, [profile?.organization_id]);

  const refresh = useMemo(
    () => async () => {
      setLoading(true);
      try {
        const [data, s] = await Promise.all([
          listInquiries({
            status: status || null,
            search: q || null,
            assignedTo: mine && profile?.id ? profile.id : null,
          }),
          getInquiryStats(),
        ]);
        setRows(data);
        setStats(s);
      } catch (err) {
        toast.error(err?.message || "Could not load inquiries");
      } finally {
        setLoading(false);
      }
    },
    [status, q, mine, profile?.id],
  );

  useEffect(() => { refresh(); }, [refresh]);

  // SLA pulse — derive from `rows` so we never need a separate query.
  // Counts only `new` inquiries (the queue); `contacted`/`archived` are
  // already taken care of.
  const slaCounts = useMemo(() => {
    const acc = { fresh: 0, aging: 0, overdue: 0 };
    for (const r of rows || []) {
      const b = inquiryAgeBucket(r);
      if (acc[b] != null) acc[b] += 1;
    }
    return acc;
  }, [rows]);

  const setFilter = (next) => {
    const nsp = new URLSearchParams(sp);
    if (!next.status) nsp.delete("status"); else nsp.set("status", next.status);
    if (!next.q)      nsp.delete("q");      else nsp.set("q", next.q);
    if (next.mine === undefined) {
      // unchanged
    } else if (next.mine) {
      nsp.set("mine", "1");
    } else {
      nsp.delete("mine");
    }
    setSp(nsp, { replace: true });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setFilter({ status, q: input.trim() });
  };

  return (
    <div data-testid="admin-inquiries" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Admin · Growth
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Enrollment inquiries
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-neutral-600">
            Every note submitted through your public site. Work them left-to-right:
            new → contacted → archived.
          </p>
        </div>
      </header>

      {/* KPI strip */}
      <section
        data-testid="admin-inquiries-kpis"
        className="grid grid-cols-2 gap-4 md:grid-cols-4"
      >
        <Kpi icon={Sparkle}     label="New this week" value={stats?.this_week}      accent="text-primary-hover" />
        <Kpi icon={Clock}       label="New"           value={stats?.new_count}      accent="text-primary-hover" />
        <Kpi icon={CheckCircle} label="Contacted"     value={stats?.contacted_count} accent="text-[#8A5A12]" />
        <Kpi icon={Archive}     label="Archived"      value={stats?.archived_count}  accent="text-neutral-500" />
      </section>

      {(slaCounts.aging + slaCounts.overdue) > 0 && (
        <section
          data-testid="admin-inquiries-sla-strip"
          className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#EFBA57]/30 bg-[#EFBA57]/5 p-3.5"
        >
          <Clock size={14} weight="duotone" className="text-[#8A5A12]" />
          <p className="text-sm text-neutral-700">
            {slaCounts.overdue > 0 && (
              <span data-testid="admin-inquiries-sla-overdue" className="font-semibold text-[#C6851E]">
                {slaCounts.overdue} {slaCounts.overdue === 1 ? "inquiry is" : "inquiries are"} overdue
                {slaCounts.aging > 0 ? " · " : ""}
              </span>
            )}
            {slaCounts.aging > 0 && (
              <span data-testid="admin-inquiries-sla-aging" className="font-semibold text-[#8A5A12]">
                {slaCounts.aging} aging past 8 h
              </span>
            )}
            {slaCounts.aging + slaCounts.overdue > 0 && (
              <span className="text-neutral-500">
                {" "}— a quick reply usually keeps the lead warm.
              </span>
            )}
          </p>
        </section>
      )}

      {/* Filter row */}
      <section className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip
            active={!status}
            onClick={() => setFilter({ status: "", q })}
            testid="filter-all"
          >
            All
          </StatusChip>
          {INQUIRY_STATUSES.map((s) => (
            <StatusChip
              key={s.key}
              active={status === s.key}
              onClick={() => setFilter({ status: s.key, q })}
              testid={`filter-${s.key}`}
            >
              {s.label}
            </StatusChip>
          ))}
          <span aria-hidden className="mx-1 h-5 w-px bg-neutral-200" />
          <StatusChip
            active={mine}
            onClick={() => setFilter({ status, q, mine: !mine })}
            testid="filter-mine"
          >
            {mine ? "✓ Assigned to me" : "Assigned to me"}
          </StatusChip>
        </div>
        <form
          onSubmit={onSearchSubmit}
          className="relative ml-auto w-full max-w-xs"
          data-testid="admin-inquiries-search-form"
        >
          <MagnifyingGlass
            size={14}
            weight="duotone"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
          />
          <input
            type="search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search by name, email, or text"
            data-testid="admin-inquiries-search-input"
            className="w-full rounded-full border border-neutral-300 bg-white py-2 pl-9 pr-4 text-sm placeholder:text-neutral-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </form>
      </section>

      {/* Table */}
      <section
        data-testid="admin-inquiries-list"
        className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div
            data-testid="admin-inquiries-empty"
            className="px-6 py-16 text-center"
          >
            <Sparkle size={28} weight="duotone" className="mx-auto text-neutral-300" />
            <p className="mt-3 font-heading text-base font-semibold text-neutral-800">
              {status || q ? "No inquiries match these filters" : "No inquiries yet"}
            </p>
            <p className="mt-1 text-sm text-neutral-500">
              {status || q
                ? "Try a different status or clear the search."
                : "Share your public site with prospective families to start collecting leads."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {rows.map((row) => (
              <InquiryRow
                key={row.id}
                row={row}
                assignee={row.assigned_to ? adminLookup[row.assigned_to] : null}
                isMine={profile?.id && row.assigned_to === profile.id}
              />
            ))}
          </ul>
        )}
      </section>

      {rows.length >= 100 && (
        <p className="text-xs text-neutral-500">
          Showing the 100 most recent. Narrow with a status filter or search.
        </p>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <article
      data-testid={`admin-inquiries-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <div className="flex items-center justify-between">
        <div className={`flex h-9 w-9 items-center justify-center rounded-xl bg-neutral-50 ${accent}`}>
          <Icon size={16} weight="duotone" />
        </div>
      </div>
      <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </p>
      <p className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">
        {value ?? "…"}
      </p>
    </article>
  );
}

function StatusChip({ active, children, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
        active
          ? "border-primary bg-primary text-white"
          : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50"
      }`}
    >
      {children}
    </button>
  );
}

function InquiryRow({ row, assignee, isMine }) {
  const s = statusMeta(row.status);
  const initial = (assignee?.full_name?.[0] || "?").toUpperCase();
  const ageBucket = inquiryAgeBucket(row);
  const ageTone = AGE_TONE[ageBucket];
  return (
    <li
      data-testid="admin-inquiry-row"
      data-id={row.id}
      data-status={row.status}
      data-assigned={assignee ? "true" : "false"}
      data-mine={isMine ? "true" : "false"}
      data-age-bucket={ageBucket}
    >
      <Link
        to={`/admin/inquiries/${row.id}`}
        className="flex items-center gap-4 px-5 py-4 transition hover:bg-neutral-50"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-heading text-sm font-semibold text-neutral-900">
              {row.parent_name}
            </p>
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0 text-[10px] font-semibold uppercase tracking-wide ${s.tone}`}
            >
              {s.label}
            </span>
            {ageTone && (
              <span
                data-testid="admin-inquiry-row-age"
                data-age-bucket={ageBucket}
                title={`Open ${formatAgeShort(row.created_at)}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0 text-[10px] font-bold uppercase tracking-wide ${ageTone.pill}`}
              >
                {ageTone.label} · {formatAgeShort(row.created_at)}
              </span>
            )}
            {assignee ? (
              <span
                data-testid="admin-inquiry-row-assignee"
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  isMine
                    ? "bg-primary text-white"
                    : "bg-primary/10 text-primary-hover"
                }`}
              >
                <span
                  className={`flex h-3.5 w-3.5 items-center justify-center rounded-full text-[9px] font-bold ${
                    isMine ? "bg-white text-primary-hover" : "bg-primary text-white"
                  }`}
                >
                  {initial}
                </span>
                <span className="hidden sm:inline">
                  {isMine ? "You" : assignee.full_name}
                </span>
              </span>
            ) : (
              <span
                data-testid="admin-inquiry-row-unassigned"
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-neutral-300 px-2 py-0.5 text-[10px] font-semibold text-neutral-500"
              >
                <UserCircle size={10} weight="duotone" />
                Unassigned
              </span>
            )}
            {row.child_age && (
              <span className="hidden text-[11px] text-neutral-500 md:inline">
                · {row.child_age}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-neutral-600">
            {row.parent_email}
            {row.parent_phone ? ` · ${row.parent_phone}` : ""}
          </p>
          <p className="mt-1 line-clamp-1 text-xs text-neutral-500">
            {row.message}
          </p>
        </div>
        <div className="flex flex-col items-end gap-0.5 text-right">
          <span className="text-[11px] font-semibold text-neutral-500">
            {new Date(row.created_at).toLocaleDateString()}
          </span>
          <span className="text-[10px] text-neutral-400">
            {new Date(row.created_at).toLocaleTimeString([], {
              hour: "numeric", minute: "2-digit",
            })}
          </span>
        </div>
        <ArrowRight size={14} weight="bold" className="text-neutral-300" />
      </Link>
    </li>
  );
}
